import { isObject } from 'src/util/object-util';
import { onIdle, onReadyStateCompleteOrLoad } from 'src/util/browser-util';
import { Helpers } from 'src/helpers';
import { ERRORS } from 'src/constants';

const KEEPALIVE_TOGGLE_DELAY = 2000;
class Publisher {
    constructor() {
        this._isScheduled = false;
        this._batchQueue = [];
        this._processEvents = this._processEvents.bind(this);
        this._onFetchError = this._onFetchError.bind(this);
        /**
         * When we're able to use the `fetch` API, determines whether requests should be sent with `keepalive` option set
         * This is updated once the initial burst of requests complete because of capacity limits on `keepalive` requests.
         */
        this.shouldFetchWithKeepalive = false;
        this._registerEventListeners();
    }
    /**
     * Submit event into the batch queue
     *
     * @param {Object} event - Event JSON object
     * @param {Function} [logger] - (optional) logger to emit events, see EgClickstream
     */
    publish(event, logger = console.error) {
        if(!isObject(event)) {
            return;
        }

        this._batchQueue.push(event);
        this._scheduleAsyncProcessing(logger);
    }

    /**
     * Schedule events processing
     * Use requestIdleCallback if available or fall back to setTimeout
     * @private
     * @param {Function} [logger] - (optional) logger to emit events, see EgClickstream
     */
    _scheduleAsyncProcessing(logger) {
        // Do nothing if the processing has already been scheduled
        if (this._isScheduled) {
            return;
        }

        this._isScheduled = true;
        onIdle(this._processEvents(logger));
    }
    /**
     * Process events stored in the batch queue
     * - send events to the collect service
     * - empty the batch queue
     * - and reset isScheduled so future processing can be scheduled
     * @private
     * @param {Function} [logger] - (optional) logger to emit events, see EgClickstream
     */
    _processEvents(logger) {
        return () => {
            const currentBatch = this._batchQueue;
            const chunks = Helpers.groupEventsByViewId(currentBatch);

            chunks.forEach(chunk => {
                Helpers.postJsonWithRetry(chunk, {
                    keepalive: this.shouldFetchWithKeepalive,
                    headers: Helpers.getHeaderValue(chunk)
                }).catch(e => {
                    this._onFetchError(e, logger);
                });
            });

            this._batchQueue = [];
            this._isScheduled = false;
        };
    }

    _onFetchError(e, logger) {
        // disable keepalive for 2 seconds for upcoming batched requests if keepalive related error occurred and keepalive is enabled
        if (this.shouldFetchWithKeepalive === true && Helpers.isKeepaliveRelated(e)) {
            this._disableKeepalive();
            setTimeout(this._enableKeepalive.bind(this), KEEPALIVE_TOGGLE_DELAY);
        }

        logger((e instanceof Error ? e : new Error(`${e}`)), ERRORS.FETCH_ERROR);
    }

    _enableKeepalive() {
        this.shouldFetchWithKeepalive = true;
    }

    _disableKeepalive() {
        this.shouldFetchWithKeepalive = false;
    }

    _registerEventListeners() {
        // start using keepalive flag in case that "something" is exit the page
        onReadyStateCompleteOrLoad(this._enableKeepalive.bind(this));
    }
}

function createPublisher() {
    return new Publisher();
}

export { createPublisher };
